/**
 * SizeSelectBottomSheet
 * Modal bottom sheet for choosing size when moving wishlist item to cart.
 * Use when product has multiple in-stock sizes so user can pick before adding.
 * UX: Buttons for sizes (Baymard), single-step, product preview (NN/G bottom sheet).
 */

import { useState, useEffect } from 'react';
import { IconClose } from '../../common/Icons/Icons';
import ApiImage from '../../common/ApiImage';
import './SizeSelectBottomSheet.css';

function SizeSelectBottomSheet({
  isOpen,
  onClose,
  product = null,
  inStockSizes = [],
  onAddToCart,
  loading = false,
  error = null,
}) {
  const [selectedSize, setSelectedSize] = useState(null);

  // Reset selection when sheet opens with new product
  useEffect(() => {
    if (isOpen && product) {
      setSelectedSize(null);
    }
  }, [isOpen, product?.id]);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const fn = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const productName = product?.name || 'Product';
  const productImage = product?.image ||
    product?.images?.[0]?.secure_url ||
    product?.images?.[0]?.url ||
    (typeof product?.images?.[0] === 'string' ? product.images[0] : null) ||
    '/hero.png';

  const handleAdd = () => {
    if (!selectedSize || !onAddToCart) return;
    onAddToCart(selectedSize);
  };

  return (
    <>
      <div
        className="size-sheet__backdrop size-sheet__backdrop--on"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`size-sheet__sheet ${isOpen ? 'size-sheet__sheet--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="size-sheet-title"
        aria-describedby="size-sheet-desc"
      >
        <div className="size-sheet__handle" aria-hidden="true" />

        <div className="size-sheet__head">
          <h2 id="size-sheet-title" className="size-sheet__title">
            Select size
          </h2>
          <button
            type="button"
            className="size-sheet__close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconClose size={20} />
          </button>
        </div>

        <div className="size-sheet__product">
          <ApiImage
            src={productImage}
            fallback="/placeholder-jewelry.svg"
            alt=""
            className="size-sheet__product-img"
            width="72"
            height="90"
          />
          <p id="size-sheet-desc" className="size-sheet__product-name">
            {productName}
          </p>
        </div>

        <div className="size-sheet__sizes">
          <p className="size-sheet__sizes-label">Size</p>
          <div className="size-sheet__sizes-list" role="group" aria-label="Choose size">
            {inStockSizes.map(({ size, stock }) => (
              <button
                key={size}
                type="button"
                className={`size-sheet__size-btn ${selectedSize === size ? 'size-sheet__size-btn--selected' : ''}`}
                onClick={() => setSelectedSize(size)}
                aria-pressed={selectedSize === size}
                aria-label={`Size ${size}${stock > 1 ? `, ${stock} in stock` : ''}`}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p className="size-sheet__error" role="alert">
            {error}
          </p>
        )}

        <div className="size-sheet__actions">
          <button
            type="button"
            className="size-sheet__add-btn"
            onClick={handleAdd}
            disabled={!selectedSize || loading}
            aria-busy={loading}
          >
            {loading ? 'Adding…' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </>
  );
}

export default SizeSelectBottomSheet;
